import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetSingleReservationQuery } from '../redux/rtkQuery/apiSlice';
import { buildRegistrationWizardSteps } from '../sections/registration/types';
import { useReservationInfo } from '../ReservationInfoContext';
import { ReservationGeneral } from '../models/ReservationGeneral';
import { RegistrationWizard } from '../sections/registration/RegistrationWizard';
import { BrandConfig } from '../config/brandConfig';

// ----------------------------------------------------------------------

type RegistrationGuardProps = {
  children: React.ReactNode;
  brand: BrandConfig;
};

//NOTE: Wherever we have the registration guard, we need a SignatureProvider, _under the current design_.
//		This is due to the rental agreement step.

export default function RegistrationGuard({ children, brand }: RegistrationGuardProps) {
  const [workingReservationName, setWorkingReservationName] = useState<string>('');
  const [reservation, setReservation] = useState<ReservationGeneral | null>(null);
  const { reservationsByName } = useReservationInfo();
  const { reservationName: pathReservationName, additionalParams } = useParams<{
    reservationName: string;
    additionalParams?: string;
  }>();
  const { enqueueSnackbar } = useSnackbar();
  const payByBankRoute = additionalParams === 'pay-by-bank';
  const rentalAgreementSignedRoute = additionalParams === 'rental-agreement-signed';
  const {
    isFetching: reservationFetching,
    isSuccess: reservationSuccess,
    isUninitialized: reservationUninitialized,
    data: reservationData,
    refetch: refetchReservation,
  } = useGetSingleReservationQuery(
    { reservationName: pathReservationName },
    {
      skip: !pathReservationName || payByBankRoute || rentalAgreementSignedRoute,
    }
  );

  const refreshReservation = useCallback(
    (overrideBusy: boolean = false) => {
      if (reservationUninitialized) {
        // Child effects run before this component's effects, so on a fresh mount a child can
        // request a refresh before RTK Query has started this query (refetch would throw).
        // The initial fetch is about to run anyway, so a manual refetch is unnecessary.
        return;
      }
      if (reservationFetching && !overrideBusy) {
        // already refetching
        return;
      }
      refetchReservation();
    },
    [refetchReservation, reservationFetching, reservationUninitialized]
  );

  useEffect(() => {
    if (!reservationData) {
      return;
    }
    setReservation(reservationData);
  }, [reservationData, reservationSuccess]);

  // find and set the reservation from the url, when applicable
  let updateReservation = false;
  if (!reservation) {
    // first-time check needed
    updateReservation = true;
  } else if (reservation.name !== pathReservationName) {
    // the selected reservation has changed
    updateReservation = true;
  }

  if (updateReservation && pathReservationName) {
    const contextReservation = reservationsByName.get(pathReservationName);
    if (contextReservation) {
      setReservation(contextReservation);
    }
  }

  if (reservation && reservation.require_guest_registration) {
    if (reservation.guest_registration_complete) {
      if (workingReservationName !== '') {
        setWorkingReservationName('');

        // mark these off in a global
        //TODO: We could build a full context for this, but they're extremely rare,
        //		and are not needed elsewhere. We don't have the time. If anything,
        //		fall back to session storage.
        if (!(window as any).drr_completedRegistrations) {
          // lazy create
          (window as any).drr_completedRegistrations = new Set<string>();
        }

        if (!(window as any).drr_completedRegistrations.has(reservation.name)) {
          (window as any).drr_completedRegistrations.add(reservation.name);

          enqueueSnackbar('Thank you for completing guest registration!', {
            variant: 'success',
            autoHideDuration: 10 * 1000,
            onClick: (x: any) => {},
          });
        }
      }
      // else already shown
    } else if (reservation.guest_registration_requirements.length > 0) {
      if (workingReservationName !== reservation.name) {
        setWorkingReservationName(reservation.name);
      }
      if (payByBankRoute || rentalAgreementSignedRoute) {
        return <>{children}</>;
      }
      const hideResortDisclosures = Object.keys(reservation.resort_disclosures).length === 0;
      const registrationSteps = buildRegistrationWizardSteps(reservation, hideResortDisclosures);
      return (
        <RegistrationWizard
          reservation={reservation}
          registrationSteps={registrationSteps}
          callbackForceGuardRefetch={(overrideBusy: boolean) => {
            refreshReservation(overrideBusy);
          }}
          brand={brand}
        />
      );
    }
  }

  return <> {children} </>;
}
